/**
 * `<audit-record-detail>` — the audit record body (`04` §7.2), shared by the
 * explorer's drawer and by the standalone record page.
 *
 * It is one element rather than two copies because the four sections it renders
 * are where this product's honesty obligations concentrate: the projection notice,
 * the Minimized-profile statement, the chain state of the surrounding range, the
 * `binding_name`-is-always-null disclosure, the edge-proxy caveat on `remote_ip`,
 * and the reconstruction-not-a-replay label on the drill-down. Two copies of that
 * is two chances to lose one.
 *
 * Implements `SC-F804` (record detail), `SC-F809` (the denial drill-down and the
 * drill-through to the policy that caused it), `SC-F821` (the Minimized statement)
 * and the record half of `SC-F803` / `SC-F807` (per-record integrity). Stories
 * `SC-S804`, `SC-S805`, `SC-S809`, `SC-S812`.
 *
 * It fetches two things of its own — the error-code catalogue and, for a record
 * with a `query_id`, its sibling record — and emits `filter-request` for every
 * chip that narrows a search, because *where* that filter lands differs between
 * the two hosts: the explorer applies it in place, the record page navigates.
 */

import { LitElement, css, html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import { decisionVariant, type BnKvRow } from "../components/index.js";

import {
  searchAudit,
  type AuditFilter,
  type AuditProfileName,
  type AuditRecord,
  type AuditRecordDetail,
} from "../api/audit.js";
import { explainPrefill, type ExplainInput } from "../api/cli.js";
import { listGenerations, type Generation } from "../api/generations.js";
import { ApiError, parseError } from "../api/errors.js";
import { hrefFor, navigate } from "../nav.js";
import {
  CHAIN_STATE_META,
  PROFILE_META,
  auditSharedCss,
  chainBadge,
  count,
  duration,
  errorCodeHelp,
  parsePolicyRef,
  policyHref,
  projectionNotice,
  shortHash,
  whenUtc,
} from "./audit-shared.js";

/**
 * SQL_SAMPLE_BYTES is what each profile renders into `audit.sqlSampleBytes`
 * (`queryaudit.Profile.SQLSampleBytes`).
 *
 * It is here to detect **truncation**, which is the one thing the record itself
 * cannot say: a sample whose byte length equals the cap was cut, and `04` §7.2
 * requires that marked so the reproduce action does not silently run a partial
 * statement. Under `customer_held` the customer chooses the value and the control
 * plane does not know it, so truncation is reported as unknown rather than guessed.
 */
const SQL_SAMPLE_BYTES: Record<AuditProfileName, number | null> = {
  standard: 2048,
  minimized: 0,
  customer_held: null,
};

/**
 * Caveat is one stated limitation of the `SC-F809` drill-down.
 *
 * The ids and the sentences are `internal/analytics`' `Caveat` vocabulary. They
 * live server-side precisely so the wording cannot drift from the condition that
 * raises it — but no route serves a `Reconstruction` yet, so they are transcribed
 * here.
 *
 * TODO(SC-F809): when a route returns `analytics.Reconstruction`, delete this table
 * and render `caveats[].explanation` off the response. `analytics.Caveat.Explanation`
 * is the source of truth in the meantime.
 */
interface Caveat {
  id: string;
  text: string;
}

const CAVEAT_TEXT: Record<string, string> = {
  sql_sample_truncated:
    "The stored SQL sample was cut at the instance's configured sample size, so the statement " +
    "explained here may parse differently from the one that ran.",
  sql_sample_absent:
    "This instance's audit privacy profile is Minimized, so no SQL text was captured at the " +
    "source — by your organization's own choice. The explanation is based on the identity and the " +
    "table only.",
  subject_claims_unknown:
    "An audit record carries only the subject's id, method, issuer, email and groups. A policy " +
    "that matched on any other token claim cannot be reproduced from it.",
  generation_attributed_by_time:
    "The configuration generation was chosen by the record's timestamp, because a record carries " +
    "no configuration digest. That is exact except within a publish window.",
  generation_unknown:
    "No configuration generation covers this record's timestamp, so the explanation cannot be " +
    "pinned to the policy set that was live at the time.",
  generation_is_live:
    "This is the generation that is live right now, so the explanation will change when the next " +
    "publish lands.",
  no_table_in_record:
    "The record names no table — the statement did not parse, or the event carries none — so " +
    "there is nothing to explain access against.",
  remote_ip_is_edge_proxy:
    "The recorded client address is the edge proxy's, not the caller's, for every record on every " +
    "instance. Nothing in this explanation depends on it.",
  column_masks_not_in_record:
    "An audit record names the masking policy but never the columns it resolved to. A masked " +
    "column list can only come from re-evaluating the policy, not from the record.",
  api_key_subject_not_modelled:
    "This subject authenticated with an API key. `policy explain` describes a JWT-shaped identity " +
    "only and cannot model an API-key subject at all (SLUICE-U27), so a policy that behaves " +
    "differently for one cannot be previewed here.",
};

/** Detail of `filter-request` — a partial filter the host should apply. */
export type FilterRequestDetail = AuditFilter;

@customElement("audit-record-detail")
export class AuditRecordDetailView extends LitElement {
  @property() instanceId = "";
  /** The record to render. Null renders nothing — the host owns the loading and
   * error states, because they are page furniture rather than record content. */
  @property({ attribute: false }) record: AuditRecordDetail | null = null;

  /** The human meaning of `error_code`, read from the form schema. */
  @state() private codeHelp = "";
  /** The record's sibling by `query_id`. `rows` and `truncated` live only on the
   * `query-result` half, so a `query` record alone cannot answer "how many rows
   * did it return" — `SC-S804` requires the pair shown together. */
  @state() private siblings: AuditRecord[] = [];
  @state() private siblingError: ApiError | null = null;
  /** The generation live when the record was written, for `SC-F809`'s pin. */
  @state() private generation: Generation | null = null;
  @state() private generationChecked = false;
  @state() private generationIsLive = false;

  static override styles = [
    auditSharedCss,
    css`
      bn-code {
        display: block;
        margin-top: var(--bino-space-sm);
      }
      .integrity-alert {
        margin: 0 0 var(--bino-space-md);
      }
      .pair {
        display: grid;
        gap: var(--bino-space-xs);
      }
      .pair-row {
        display: flex;
        align-items: center;
        gap: var(--bino-space-sm);
        flex-wrap: wrap;
        font-size: var(--bino-font-size-md);
      }
      ul.caveats {
        margin: var(--bino-space-sm) 0 0;
        padding-left: 1.1rem;
        font-size: var(--bino-font-size-md);
        color: var(--bino-text-muted);
      }
      ul.caveats li {
        margin-bottom: var(--bino-space-xs);
      }
      a.plain {
        color: var(--bino-primary);
      }
    `,
  ];

  override updated(changed: PropertyValues<this>) {
    if (!changed.has("record")) return;
    const rec = this.record;
    this.codeHelp = "";
    this.siblings = [];
    this.siblingError = null;
    this.generation = null;
    this.generationChecked = false;
    this.generationIsLive = false;
    if (!rec) return;
    if (rec.error_code) void this.loadCodeHelp(rec.error_code);
    if (rec.query_id) void this.loadSiblings(rec);
    void this.loadGeneration(rec);
  }

  private async loadCodeHelp(code: string) {
    this.codeHelp = await errorCodeHelp(code);
  }

  /**
   * loadSiblings finds the other half of the `query` / `query-result` pair.
   *
   * The window is bracketed around the record's own timestamp rather than left to
   * default: the search route's implicit range is the trailing 24 hours, so a
   * record from last week would find nothing and the absence would read as "there
   * is no result record" instead of "you searched the wrong day".
   */
  private async loadSiblings(rec: AuditRecordDetail) {
    const at = new Date(rec.ts).getTime();
    if (Number.isNaN(at)) return;
    try {
      const page = await searchAudit(this.instanceId, {
        query_id: [rec.query_id ?? ""],
        from: new Date(at - 60_000).toISOString(),
        to: new Date(at + 3_600_000).toISOString(),
        limit: 10,
      });
      this.siblings = page.items.filter((r) => r.id !== rec.id);
    } catch (err) {
      // A failure here costs the pairing, not the record. It is reported inline
      // rather than swallowed, because "no result record" and "we could not look"
      // are different answers to "did this query return rows".
      this.siblingError = parseError(err);
    }
  }

  /**
   * loadGeneration resolves which configuration generation was live when the
   * record was written (`SC-S809`'s last criterion).
   *
   * Attribution is **by timestamp**: the record carries no configuration digest,
   * which is `04` §6.4's own entry and the reason `generation_attributed_by_time`
   * is always a stated caveat. A generation is live from its `published_at` until
   * the next successfully published one, so the answer is the newest generation
   * published at or before the record.
   */
  private async loadGeneration(rec: AuditRecordDetail) {
    const at = new Date(rec.ts).getTime();
    try {
      const page = await listGenerations(this.instanceId, { limit: 200 });
      const published = page.items
        .filter((g) => !!g.published_at)
        .sort(
          (a, b) => new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime(),
        );
      const found = published.find((g) => new Date(g.published_at ?? 0).getTime() <= at) ?? null;
      this.generation = found;
      this.generationIsLive = !!found && published[0]?.generation === found.generation;
    } catch {
      // No history is "unknown", never "the current draft": pinning a reader to
      // whatever is live now would link them to a policy that may have changed
      // since the denial, which is worse than no link.
      this.generation = null;
    } finally {
      this.generationChecked = true;
    }
  }

  private requestFilter(filter: AuditFilter) {
    this.dispatchEvent(
      new CustomEvent<FilterRequestDetail>("filter-request", {
        detail: filter,
        bubbles: true,
        composed: true,
      }),
    );
  }

  /* ── section 1: decision ─────────────────────────────────────────────── */

  private renderDecision(rec: AuditRecordDetail) {
    const policies = (rec.applied_policies ?? []).map(parsePolicyRef);
    // `SC-S809`'s third criterion: a deny with no applied policy is Sluice's
    // default-deny, and the copy must say so rather than naming a denying policy
    // that does not exist.
    const defaultDeny = rec.decision === "deny" && policies.length === 0;
    return html`<section class="section">
      <h3>Decision</h3>
      <div class="row">
        ${rec.decision
          ? html`<bn-badge .variant=${decisionVariant(rec.decision)}>${rec.decision}</bn-badge>`
          : html`<span class="meta">no decision on this event type</span>`}
        <bn-badge variant="neutral">${rec.event_type}</bn-badge>
        ${rec.truncated ? html`<bn-badge variant="warning">result truncated</bn-badge>` : nothing}
        ${rec.rewrite_applied
          ? html`<bn-badge variant="info">rewritten</bn-badge>`
          : nothing}
      </div>
      <bn-kv
        .rows=${[
          { label: "Time", value: whenUtc(rec.ts) },
          { label: "Query id", value: rec.query_id || "" },
          { label: "Error code", value: rec.error_code || "" },
          { label: "Message", value: rec.message || "" },
          { label: "Origin", value: rec.origin || "" },
        ] as BnKvRow[]}
      ></bn-kv>
      ${rec.error_code && this.codeHelp
        ? html`<p class="meta">${this.codeHelp}</p>`
        : nothing}
      ${defaultDeny
        ? html`<p class="notice notice-warn">
            <strong>No policy allowed this request.</strong> Sluice denies a query with no matching
            <code>SqlAccessPolicy</code> allow, and this record names no applied policy — so there
            is no denying policy to open. The fix is a policy that allows, not a policy to change.
          </p>`
        : nothing}
      <h4 class="meta">Applied policies</h4>
      ${policies.length === 0
        ? html`<p class="meta">
            None. A record with no applied policies is either a default-deny (above) or an event
            type that reaches no policy at all.
          </p>`
        : html`<div class="chips">
              ${policies.map((ref) => {
                const href = policyHref(this.instanceId, ref);
                return href
                  ? html`<a class="chip" href=${href}>${ref.kind}/${ref.name}</a>`
                  : html`<span class="chip" aria-disabled="true" title=${
                      "This reference has no kind, so it cannot be resolved to an editor route."
                    }>${ref.raw}</span>`;
              })}
            </div>
            ${this.renderGenerationPin()}`}
    </section>`;
  }

  /**
   * renderGenerationPin states what the policy links actually open.
   *
   * `SC-S809`'s last criterion is that a policy link resolves to the object **at
   * the generation that was live when the record was written**, and the route table
   * has no generation-scoped policy path — a policy editor is addressed by
   * `(kind, name)` and always shows the current draft. Rather than link to the
   * draft and imply otherwise, the pin is named in words and the
   * generation-pinned artifact that *does* exist is linked beside it: that
   * generation's read-only rendered YAML, which is the documented control
   * (`SC-F602`, `SC-F611`).
   */
  private renderGenerationPin() {
    if (!this.generationChecked) return nothing;
    const gen = this.generation;
    if (!gen) {
      return html`<p class="meta">
        No configuration generation covers this record's timestamp, so these policies cannot be
        pinned to the set that was live at the time. The links open the <strong>current
        draft</strong>, which may have changed since.
      </p>`;
    }
    return html`<p class="meta">
      Generation <strong>${gen.generation}</strong> was live when this record was written
      (published ${whenUtc(gen.published_at)})${this.generationIsLive
        ? ", and it is still live — this attribution will change at the next publish"
        : ""}. The links above open each policy's <strong>current draft</strong>; the policy set as
      it stood at the time is the
      <a class="plain" href=${hrefFor("instances", this.instanceId, "generations", gen.generation, "yaml")}
        >rendered YAML of generation ${gen.generation}</a
      >.
    </p>`;
  }

  /* ── section 2: query ────────────────────────────────────────────────── */

  private renderQuery(rec: AuditRecordDetail) {
    const cap = SQL_SAMPLE_BYTES[rec.profile];
    const sampleBytes = rec.sql_sample ? new TextEncoder().encode(rec.sql_sample).length : 0;
    const truncated = cap !== null && cap > 0 && sampleBytes >= cap;
    return html`<section class="section">
      <h3>Query</h3>
      ${this.renderSample(rec, truncated, cap)}
      <bn-kv
        unsetText="not on this record type"
        .rows=${[
          { label: "SQL fingerprint", value: rec.sql_fingerprint || "" },
          { label: "Post-rewrite fingerprint", value: rec.rewritten_fingerprint || "" },
          { label: "Duration", value: duration(rec.duration_ms), copyable: false },
          { label: "Rows returned", value: rec.rows === undefined ? "" : count(rec.rows), copyable: false },
          { label: "Truncated", value: String(rec.truncated), copyable: false },
        ] as BnKvRow[]}
      ></bn-kv>
      <p class="meta">
        <strong>${rec.rewrite_applied ? "Rewritten" : "Unchanged"}</strong> — derived from whether
        the two fingerprints differ, which answers whether the policy set actually modified the
        statement. A policy can apply and change nothing, so the applied-policy list cannot answer
        it. The product never holds rewritten SQL: there is no <code>rewritten_sql</code> field
        upstream, so the shape is answerable and the text is not.
      </p>
      <p class="meta">
        Searching by fingerprint is <strong>not offered</strong>: this API version's audit filter
        allowlist has no <code>sql_fingerprint</code> parameter, and a button that answered
        <code>422</code> would be worse than none. Copy the value and use it against an export.
      </p>
      ${rec.rows === undefined && rec.event_type === "query"
        ? html`<p class="meta">
            Row count and truncation are absent on a <code>query</code> record by design: they live
            only on the paired <code>query-result</code> record, which is written when the stream
            closes.
          </p>`
        : nothing}
      ${this.renderClamp(rec)}
      ${this.renderTargets(rec)}
      ${rec.client_meta
        ? html`<h4 class="meta">Client metadata</h4>
            <bn-kv
              .rows=${Object.entries(rec.client_meta).map(([k, v]) => ({ label: k, value: v }))}
            ></bn-kv>
            ${rec.client_meta.remote_ip
              ? html`<p class="meta">
                  <code>remote_ip</code> holds the <strong>edge proxy's</strong> address, not the
                  caller's, for every record on every instance until <code>SLUICE-U12</code> lands
                  <code>rest.trustedProxies</code>. It is shown with that explanation rather than
                  displayed as if it meant something, and it is deliberately not offered as a
                  filter: a filter over it matches everything or nothing.
                </p>`
              : nothing}`
        : nothing}
    </section>`;
  }

  /**
   * renderSample renders the SQL sample, or says exactly why there is none.
   *
   * Three different absences, three different sentences. Rendering an empty field
   * for any of them would imply the record had no SQL, which is the failure
   * `SC-F821` and `06` §3.12.1 both exist to prevent.
   */
  private renderSample(rec: AuditRecordDetail, truncated: boolean, cap: number | null) {
    if (rec.profile === "minimized") {
      return html`<p class="notice">
        <strong>No SQL text was captured.</strong> ${PROFILE_META.minimized.note} This is not a
        redaction applied on arrival and it is not missing data: the instance renders
        <code>audit.sqlSampleBytes: 0</code>, so the text never left it.
      </p>`;
    }
    if (rec.projection.applied && !rec.sql_sample) {
      return html`<p class="notice">
        The SQL sample is <strong>withheld from this response</strong> — see the redaction notice
        above. The record has one; you are not entitled to it with this role or credential class.
      </p>`;
    }
    if (!rec.sql_sample) {
      return html`<p class="meta">
        This record carries no SQL sample. Not every event type has a statement — a
        <code>genesis</code> or an <code>admin-action</code> record has none.
      </p>`;
    }
    return html`
      <bn-code
        language="sql"
        readonly
        label="SQL sample"
        .value=${rec.sql_sample}
        filename=${rec.query_id ? `${rec.query_id}.sql` : "sample.sql"}
      ></bn-code>
      ${truncated
        ? html`<p class="notice notice-warn">
            <strong>Sampled — first ${cap} bytes.</strong> The statement reached this instance's
            configured sample size, so what is shown is a prefix. Anything reconstructed from it may
            parse differently from the statement that ran, or not at all.
          </p>`
        : nothing}
    `;
  }

  /** renderClamp summarises `extras.rewrite` — the only per-record evidence that a
   * limit, a timeout or a sampling clamp actually bit. */
  private renderClamp(rec: AuditRecordDetail) {
    const rewrite = rec.extras?.["rewrite"];
    if (!rewrite || typeof rewrite !== "object") return nothing;
    const r = rewrite as Record<string, unknown>;
    const rows: BnKvRow[] = Object.entries(r).map(([k, v]) => ({
      label: k,
      value: typeof v === "object" ? JSON.stringify(v) : String(v),
    }));
    if (rows.length === 0) return nothing;
    return html`<h4 class="meta">Clamps applied</h4>
      <bn-kv .rows=${rows}></bn-kv>
      <p class="meta">
        From the record's <code>extras.rewrite</code> block. This is the only per-record evidence
        that a row limit, a statement timeout or a sampling clamp actually bit.
      </p>`;
  }

  /** renderTargets renders tables and catalogs as filter chips. Both are in the
   * server's filter allowlist, so these narrow a real search. */
  private renderTargets(rec: AuditRecordDetail) {
    const tables = rec.tables ?? [];
    const catalogs = rec.catalogs ?? [];
    if (tables.length === 0 && catalogs.length === 0) return nothing;
    return html`
      ${tables.length > 0
        ? html`<h4 class="meta">Tables</h4>
            <div class="chips">
              ${tables.map(
                (t) => html`<button
                  class="chip"
                  type="button"
                  title="Search this instance's audit for this table"
                  @click=${() => this.requestFilter({ table: [t] })}
                >
                  ${t}
                </button>`,
              )}
            </div>`
        : nothing}
      ${catalogs.length > 0
        ? html`<h4 class="meta">Catalogs</h4>
            <div class="chips">
              ${catalogs.map(
                (c) => html`<button
                  class="chip"
                  type="button"
                  title="Search this instance's audit for this catalog"
                  @click=${() => this.requestFilter({ catalog: [c] })}
                >
                  ${c}
                </button>`,
              )}
            </div>`
        : nothing}
    `;
  }

  /* ── section 3: identity ─────────────────────────────────────────────── */

  private renderIdentity(rec: AuditRecordDetail) {
    const s = rec.subject;
    return html`<section class="section">
      <h3>Identity</h3>
      <bn-kv
        .rows=${[
          { label: "Subject", value: s.sub || "" },
          { label: "Issuer", value: s.issuer || "" },
          { label: "Auth method", value: s.method || "" },
          rec.projection.applied && !s.email
            ? { label: "Email", redacted: true }
            : { label: "Email", value: s.email || "" },
          rec.projection.applied && !s.groups
            ? { label: "Groups", redacted: true }
            : { label: "Groups", value: (s.groups ?? []).join(", ") },
        ] as BnKvRow[]}
      ></bn-kv>
      ${s.sub
        ? html`<div class="row row-actions">
            <bn-button size="sm" @click=${() => this.requestFilter({ subject: [s.sub ?? ""] })}>
              Search this subject
            </bn-button>
          </div>`
        : nothing}
      <p class="meta">
        <code>binding_name</code> is <strong>always null</strong> on this build. The field is
        declared in Sluice's audit record and populated by nothing, so the key is returned as null
        rather than omitted — stating the fact instead of hiding it. Which
        <code>SubjectBinding</code> matched is not recoverable from a record.
      </p>
      ${s.method === "apikey"
        ? html`<p class="notice notice-warn">
            This subject authenticated with an <strong>API key</strong>. An API-key subject carries
            only the literal claims the key stamps, and <code>policy explain</code> models a
            JWT-shaped identity only (<code>SLUICE-U27</code>) — so a policy that behaves
            differently for an API-key consumer cannot be previewed at all.
          </p>`
        : nothing}
    </section>`;
  }

  /* ── section 4: integrity ────────────────────────────────────────────── */

  private renderIntegrity(rec: AuditRecordDetail) {
    const i = rec.integrity;
    const meta = CHAIN_STATE_META[i.chain_state];
    return html`<section class="section">
      <h3>Integrity</h3>
      ${i.verified
        ? nothing
        : html`<p class="notice notice-error integrity-alert" role="alert">
            <strong>This record's own bytes do not recompute to the hash it stores.</strong> The raw
            line is still served below — it is the evidence of the break. Do not repair, re-hash or
            re-chain it: doing so destroys the evidentiary value that made this worth reporting.
          </p>`}
      <div class="row">
        ${chainBadge(i.chain_state)}
        <bn-badge .variant=${i.verified ? "success" : "error"}>
          ${i.verified ? "hash recomputes" : "hash mismatch"}
        </bn-badge>
        ${i.recovered ? html`<bn-badge variant="warning">recovered range</bn-badge>` : nothing}
        ${i.mode === "tolerant" ? html`<bn-badge variant="warning">tolerant hash</bn-badge>` : nothing}
      </div>
      <p class="meta">${meta?.note ?? ""}</p>
      <bn-kv
        .rows=${[
          { label: "Hash", value: rec.id },
          { label: "Prior hash", value: rec.prior_hash || "" },
          { label: "Object key", value: i.object_key },
          { label: "Line", value: String(i.line_no), copyable: false },
          { label: "Byte offset", value: String(i.byte_offset), copyable: false },
          { label: "Sequence", value: String(rec.chain.seq), copyable: false },
          { label: "Recompute mode", value: i.mode, copyable: false },
        ] as BnKvRow[]}
      ></bn-kv>
      ${i.mode === "tolerant" && !i.recovered
        ? html`<p class="notice notice-warn">
            This line was verified on the <strong>tolerant</strong> path — its hash was recomputed
            from the parsed fields rather than from the stored bytes. Outside a recovered range that
            means either a new Sluice serialization or a mangled line, and it is alerted on.
          </p>`
        : nothing}
      ${i.recovered
        ? html`<p class="notice notice-warn">
            This record was <strong>recovered</strong> through the instance's admin tail and
            re-canonicalized here. It verifies, but “byte-identical to what your instance wrote”
            does not hold for it, and an export whose range overlaps it says so in the manifest.
          </p>`
        : nothing}
      ${this.renderRaw(rec)}
    </section>`;
  }

  /** renderRaw shows the raw wire line — the proof surface. Everything above it in
   * this element is *our interpretation*; the line is the artifact. */
  private renderRaw(rec: AuditRecordDetail) {
    if (rec.raw) {
      return html`
        <h4 class="meta">Raw line</h4>
        <bn-code
          language="plain"
          readonly
          label="Raw audit line"
          filename=${rec.integrity.object_key.split("/").pop() ?? "record.jsonl"}
          .value=${rec.raw}
        ></bn-code>
        <p class="meta">
          The exact bytes the instance wrote, fetched from object storage by key and offset. Hand
          this to <code>sluice audit verify</code> or your own tooling and recompute the hash
          yourself — that independence is the point. Keys are sorted and <code>hash</code> is
          appended last; the live tail's JSON is <em>not</em> this and cannot be used here.
        </p>
      `;
    }
    return html`<h4 class="meta">Raw line</h4>
      <bn-kv .rows=${[{ label: "sha256 of the raw line", value: rec.raw_digest ?? "" }]}></bn-kv>
      <p class="meta">
        The raw line is withheld from this response, because it reproduces every projected field
        exactly. The digest above is returned in its place: it proves the record was not altered in
        transit without disclosing its contents.
      </p>`;
  }

  /* ── the query / query-result pair ───────────────────────────────────── */

  private renderPair(rec: AuditRecordDetail) {
    if (!rec.query_id) return nothing;
    return html`<section class="section">
      <h3>Records for this query id</h3>
      ${this.siblingError
        ? html`<p class="notice notice-warn">
            The paired record could not be looked up (<code>${this.siblingError.code}</code>), so
            this is “we could not look”, not “there is no result record”.
          </p>`
        : this.siblings.length === 0
          ? html`<p class="meta">
              No other record carries this query id. A successful query writes two —
              <code>query</code> and <code>query-result</code> — so a lone <code>query</code> record
              means the stream never closed, or its partner has not been ingested yet.
            </p>`
          : html`<div class="pair">
              ${this.siblings.map(
                (s) => html`<div class="pair-row">
                  <bn-badge variant="neutral">${s.event_type}</bn-badge>
                  <span class="mono">${whenUtc(s.ts)}</span>
                  ${s.rows === undefined ? nothing : html`<span class="meta">${count(s.rows)} rows</span>`}
                  ${s.truncated ? html`<bn-badge variant="warning">truncated</bn-badge>` : nothing}
                  <a class="chip" href=${hrefFor("instances", this.instanceId, "audit", s.id)}>
                    ${shortHash(s.id)}
                  </a>
                </div>`,
              )}
            </div>`}
    </section>`;
  }

  /* ── SC-F809: the reconstruction ─────────────────────────────────────── */

  /**
   * caveats computes the stated limitations of the drill-down from the record.
   *
   * They are rendered **before** the action, not after the result: `04` §7.3 makes
   * these three things the UI must state rather than hide, and a caveat shown
   * after a reader has already read an answer is a caveat that arrives too late.
   */
  private caveats(rec: AuditRecordDetail): Caveat[] {
    const out: string[] = [];
    const cap = SQL_SAMPLE_BYTES[rec.profile];
    const sampleBytes = rec.sql_sample ? new TextEncoder().encode(rec.sql_sample).length : 0;
    if (rec.profile === "minimized") out.push("sql_sample_absent");
    else if (cap !== null && cap > 0 && sampleBytes >= cap) out.push("sql_sample_truncated");
    out.push("subject_claims_unknown");
    if (!this.generationChecked || this.generation) out.push("generation_attributed_by_time");
    if (this.generationChecked && !this.generation) out.push("generation_unknown");
    if (this.generationIsLive) out.push("generation_is_live");
    if ((rec.tables ?? []).length === 0) out.push("no_table_in_record");
    if (rec.client_meta?.remote_ip) out.push("remote_ip_is_edge_proxy");
    if ((rec.applied_policies ?? []).some((p) => p.startsWith("ColumnMaskPolicy/"))) {
      out.push("column_masks_not_in_record");
    }
    if (rec.subject.method === "apikey") out.push("api_key_subject_not_modelled");
    return out.map((id) => ({ id, text: CAVEAT_TEXT[id] ?? id }));
  }

  /**
   * explainInput reconstructs the explain arguments from the record — `04` §7.3's
   * mapping: `--user` from `subject.sub`, `--issuer` from `subject.issuer`,
   * `--groups` from `subject.groups`, `--table` from `tables[0]`, `--sql` from
   * `sql_sample`.
   *
   * **One deviation from the document, forced by the engine:** `policy explain`
   * takes `sql` **xor** `table` — the CLI's own rule, enforced by the API — so
   * both cannot be sent. The statement wins when there is one, because it is the
   * stronger reconstruction; under Minimized the table is used and the caveat list
   * says so. The remaining tables of a multi-table statement are named in the copy
   * rather than dropped: a cross-catalog join denied on its second table would
   * otherwise produce an explain that says "allowed" against the first.
   */
  private explainInput(rec: AuditRecordDetail): ExplainInput {
    const table = (rec.tables ?? [])[0];
    return {
      target: this.generation ? this.generation.generation : "live",
      subject: {
        user: rec.subject.sub ?? "",
        issuer: rec.subject.issuer,
        email: rec.subject.email,
        groups: rec.subject.groups,
      },
      ...(rec.sql_sample ? { sql: rec.sql_sample } : table ? { table } : {}),
    };
  }

  private renderReconstruction(rec: AuditRecordDetail) {
    const input = this.explainInput(rec);
    const reproducible = !!input.subject.user && (!!input.sql || !!input.table);
    const others = (rec.tables ?? []).slice(1);
    return html`<section class="section">
      <h3>Why was this decided?</h3>
      <bn-panel eyebrow="This is a reconstruction, not a replay">
        <p>
          The record says <em>what</em> was decided and <em>which</em> policies matched. It does not
          carry the composed reasoning. Running an explain re-evaluates a reconstructed input
          against a policy bundle — it does not re-run the original decision, and the result may
          differ from it for any of the reasons below.
        </p>
        <ul class="caveats">
          ${this.caveats(rec).map((c) => html`<li>${c.text}</li>`)}
        </ul>
        ${others.length > 0
          ? html`<p class="meta">
              This statement touched ${others.length + 1} tables. The explain is about
              <code>${input.table ?? (rec.tables ?? [])[0]}</code>; the others are
              <code>${others.join(", ")}</code>. A join denied on its second table produces an
              explain that says “allowed” about the first.
            </p>`
          : nothing}
        ${input.sql
          ? html`<p class="meta">
              Explaining the <strong>statement</strong>. <code>policy explain</code> takes a
              statement or a table, never both, so the table is derived from the statement's own
              parse.
            </p>`
          : input.table
            ? html`<p class="meta">
                Explaining the <strong>table</strong> <code>${input.table}</code>, because no
                statement is available for this record.
              </p>`
            : nothing}
        <div class="row row-actions">
          <bn-button
            variant="primary"
            ?disabled=${!reproducible}
            @click=${() => navigate(hrefFor("instances", this.instanceId, "explain") + explainPrefill(input))}
          >
            Reproduce in explain (dry run)
          </bn-button>
          ${reproducible
            ? nothing
            : html`<span class="meta">
                This record cannot be reproduced: an explain needs a subject and either a statement
                or a table, and this one is missing at least one of them.
              </span>`}
        </div>
        <p class="meta">
          The explain runs against
          ${this.generation
            ? html`generation <strong>${this.generation.generation}</strong> — the bundle that was
                live when this record was written`
            : html`the <strong>live</strong> generation, because no published generation covers this
                record's timestamp`}. Nothing is executed and no datasource is contacted: an explain
          returns a decision, never a row.
        </p>
      </bn-panel>
    </section>`;
  }

  override render() {
    const rec = this.record;
    if (!rec) return nothing;
    return html`
      <div class="frame">
        ${chainBadge(rec.integrity.chain_state)}
        <bn-badge .variant=${PROFILE_META[rec.profile]?.variant ?? "neutral"}>
          Audit profile: ${PROFILE_META[rec.profile]?.label ?? rec.profile}
        </bn-badge>
      </div>
      ${projectionNotice(rec.projection)} ${this.renderDecision(rec)} ${this.renderQuery(rec)}
      ${this.renderIdentity(rec)} ${this.renderPair(rec)} ${this.renderIntegrity(rec)}
      ${this.renderReconstruction(rec)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "audit-record-detail": AuditRecordDetailView;
  }
}

/** recordDetailTemplate is how both hosts mount the element, so the property
 * bindings and the `filter-request` wiring exist once rather than twice. */
export function recordDetailTemplate(
  instanceId: string,
  record: AuditRecordDetail | null,
  onFilter: (f: AuditFilter) => void,
): TemplateResult {
  return html`<audit-record-detail
    .instanceId=${instanceId}
    .record=${record}
    @filter-request=${(e: CustomEvent<FilterRequestDetail>) => onFilter(e.detail)}
  ></audit-record-detail>`;
}
